import { useEffect } from "react";
import { useMember } from "@/contexts/memberContext";
import { useBookingSocketListener } from "@/hooks/useBookingSocketListener";

/**
 * Invisible companion mounted inside MemberProvider. Keeps the member record in sync with the
 * server so a role/permission change (e.g. promotion to Practice Manager) reflects live —
 * without asking the member to log out and back in. Three triggers, all funnelling into
 * `syncMemberFromServer`:
 *
 *  - on mount: reconciles a member who was promoted while already logged in, the next time the
 *    app loads;
 *  - on tab focus / visibility: the fallback that catches anything the socket missed;
 *  - on the server-pushed `member_updated` socket event: the instant path.
 *
 * It also owns the shared booking socket listener (relocated here from TPR) so booking
 * mutations continue to invalidate caches across browsers.
 */
export const LiveMemberRefresh = () => {
  const { member, syncMemberFromServer } = useMember();

  // Instant path + existing booking-event handling on one shared socket.
  useBookingSocketListener(!!member, { onMemberUpdated: syncMemberFromServer });

  // Reconcile once on mount.
  useEffect(() => {
    syncMemberFromServer();
  }, [syncMemberFromServer]);

  // Fallback: refetch when the member returns to the tab.
  useEffect(() => {
    const onFocus = () => syncMemberFromServer();
    const onVisibility = () => {
      if (document.visibilityState === "visible") syncMemberFromServer();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [syncMemberFromServer]);

  return null;
};
